import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  RefObject,
} from 'react';
import * as GeoMap from '@llkc/manslauks';
import '@llkc/manslauks/dist/manslauks.css';
import { useSelectedRows } from '../../../../../components/common/table/selected-rows-context';
import Field from '../../../../../interfaces/field';
import { route, uuid } from '../../../../../services/utils';
import {
  CROP_FIELD_SPLIT,
  CROP_FIELD_VIEW,
  CROP_FIELDS_MERGE,
} from '../../../../../router/routes';
import TabsKeys from '../../../../../enums/tab-keys';
import DrawTypes from '../../../../../enums/draw-type';
import MapFieldType from '../../../../../enums/map-field-type';
import { useHistory, useParams } from 'react-router-dom';
import indexCss from './newmap.module.css';
import { IConfirmDecision } from '@llkc/manslauks/dist/map/interfaces';
import { useTranslation } from 'react-i18next';
import {
  ButtonComponent,
  ButtonStyling,
} from '../../../../../components/form/elements/button';
import css from '../../../../../components/form/elements/checkbox.module.css';
import { useFieldsForMapping } from '../basic-info/components/synchronize-context';
import { Feature as GeoJSONFeature } from 'geojson';
import debounce from 'awesome-debounce-promise';
import { IMapComponentProps } from '@llkc/manslauks/dist/map/manslauks';
import { useDispatch } from 'react-redux';
import {
  setSplitGeometries,
  setMergeGeometries,
} from '../../../../../store/global-slice';
import useColumn from '../../../../../components/common/table/use-column';
import checkBoxCss from '../../../../../components/form/elements/checkbox.module.css';
import LegendItem from './legend-item';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import MapActionType from '../../../../../enums/map-action-type';
import Spinner from '../../../../../components/common/spinner';

interface SelectedEditFeatureProps {
  id: string;
  timestamp: number;
}

interface Coordinate extends Iterable<number> {
  x: number;
  y: number;
}

export interface RedirectFeature {
  geometry: string;
  area: string;
  name: string;
  lad_number: string;
  crop_id: { value: string; label: string };
  crop_group_id: { value: string; label: string };
  breed: string;
  mergeCandidates: string[];
}

export const searchInputs = [
  {
    id: 'adreses',
    labelField: 'ADRPILNS',
  },
  {
    id: 'vietvardi',
    labelField: 'NAMELV',
  },
  {
    id: 'cadaster',
    labelField: 'CODE',
  },
  {
    id: 'lad',
    labelField: 'BLOCK_NUMBER',
  },
];

const ladFieldId = 'LADFields';

const availableLayers = [
  {
    id: ladFieldId,
    title: 'pages.crop.fields_and_works.fields.layers_lad_fields',
  },
  {
    id: 'LADFieldBlocks',
    title: 'pages.crop.fields_and_works.fields.layers_lad_field_blocks',
  },
  {
    id: 'BioValuableGrass',
    title: 'pages.crop.fields_and_works.fields.layers_bio',
  },
  {
    id: 'VZDcadasterWMS',
    title: 'pages.crop.fields_and_works.fields.layers_vzd',
  },
  {
    id: 'Zmni',
    title: 'pages.crop.fields_and_works.fields.layers_zmni',
  },
];

const timeStampRandomNumber = 258;
const spatialReferenceSystemIdentifier = 'SRID=3059';
const mapLoadTimeout = 2000;
const searchDebounceTimeout = 500;
const enterKeyCode = 13;
const tableConfigurationConfig = 'FIELDS';
const ladDataStoredType = 'LAD';
const layerWithLegend = 'Zmni';
const splitLength = 2;

const NewMap = ({
  data,
  isEditActive = false,
  isDrawingActive = false,
  isIdentifyActive,
  setField,
  isSplitActive = false,
  isMergeActive = false,
  zoomToMainFeature = false,
  setValue,
  mainFieldId,
  triggerZoom,
  type,
  deleteWaypoint,
  submitted,
  mapLoaded,
  hasGeometryChanged,
}: any) => {
  const [featureData, setFeaturedData] = useState([]);
  const [selectedFields, setSelectedFields] = useSelectedRows<Field>();
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
  const [selectForEditFeatureId, setSelectForEditFeatureId] = useState<
    SelectedEditFeatureProps | undefined
  >();
  const [isDisableVisible, setIsDisableVisible] = useState<boolean>();
  const [deletePromptDecision, setIsDisableData] = useState<IConfirmDecision>();
  const [mergePromptDecision, setMergePromptDecision] =
    useState<IConfirmDecision>();
  const [toggleLayerMenu, setToggleLayerMenu] = useState<boolean>();
  const [checkedLayerItems, setCheckedLayerItems] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useState({
    text: '',
    maxResults: 50,
    minTextLength: 3,
    minNumberLength: 3,
  });
  const [searchOptions, setSearchOptions] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [previewFeatures, setPreviewFeatures] = useState([]);
  const [zoomToFeature, setZoomToFeature] = useState<GeoJSONFeature[]>(null);
  const [tooltipHtml, setTooltipHtml] = useState<
    HTMLDivElement | TrustedHTML | null
  >();
  const [isMapReady, setIsMapReady] = useState<boolean>(false);
  const [showLegends, setShowLegends] = useState<boolean>();
  const [legendList, setLegendList] = useState([]);
  const [splitFeatures, setSplitFeatures] = useState([]);
  const [mergeFeatures, setMergeFeatures] = useState([]);
  const [mergeCandidates, setMergeCandidates] = useState<string[]>([]);
  const [hasIdentified, setHasIdentified] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [showError, setShowError] = useState<string | undefined>();

  const dispatch = useDispatch();
  const { columnConfiguration } = useColumn({
    config: tableConfigurationConfig,
  });
  const layerRef = useRef(null);
  const searchInputRef = useRef(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const removableSubFeature = deleteWaypoint
    ? featureData.find((feature) => feature.properties.ID === deleteWaypoint)
    : null;

  const {
    mappingState,
    fieldSearchForMappingState,
    currentLadStatusWithLadState,
    fieldsNotRegisteredInLadState,
  } = useFieldsForMapping();

  const onSearch = async (text: string) => {
    setPreviewFeatures([]);
    setSearchParams({
      ...searchParams,
      text: text,
    });
  };

  const debounceSearch = debounce(onSearch, searchDebounceTimeout);

  const history = useHistory();
  const { t } = useTranslation();
  const { farmId, season } = useParams<{
    farmId: string;
    season: string;
  }>();

  const createUniqueTimestamp = () => Math.random() * timeStampRandomNumber;

  useEffect(() => {
    if (!data) {
      return;
    }

    const polygons = handlePolygons(data, undefined);
    const waypoints = handleWaypoints(data);

    const hasPolygonChanged =
      JSON.stringify(
        featureData.filter((e) => e.geometry.type === DrawTypes.Polygon)
      ) !== JSON.stringify(polygons);

    //disable rerender because this is read only
    if (
      featureData?.length &&
      type !== MapActionType.default &&
      type !== MapActionType.split &&
      type !== MapActionType.identify
    ) {
      return;
    }

    if (
      polygons.length >= 1 &&
      isIdentifyActive &&
      mainFieldId === polygons[0].id
    ) {
      setTimeout(
        () => {
          setSelectForEditFeatureId({
            id: polygons[0].id,
            timestamp: createUniqueTimestamp(),
          });
        },
        isMapReady ? 0 : mapLoadTimeout //on initial render map is not ready so it needs a timeout
      );
    }

    if (triggerZoom) {
      setZoomToFeature(polygons);
    }

    //for some reason triggers rerender even though nothing has changed
    //on edit or draw only rerender on point change
    if (
      isDisableVisible ||
      (isEditActive
        ? featureData?.length !== [...polygons, ...waypoints].length
        : hasPolygonChanged)
    ) {
      if (isDrawingActive && dataLoaded) {
        return;
      }

      setFeaturedData([...polygons, ...waypoints]);
      // setDataLoaded(true);
      setIsDisableVisible(false);

      if (isIdentifyActive && hasIdentified) {
        setSelectedFeatureIds([polygons[0].id]);
      }
    }

    if (mapLoaded) {
      setDataLoaded(true);
    }
  }, [data, isMapReady, hasGeometryChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  //on table selecting entries
  useEffect(() => {
    setSelectedFeatureIds(selectedFields.map((field) => field['id']));
  }, [selectedFields]);

  useEffect(() => {
    const onError = (e) => {
      if (e?.detail?.error === 'TopologyException') {
        setShowError('error_topology');
      }
    };

    document.addEventListener('mapError', onError);

    return () => document.removeEventListener('mapError', onError);
  }, [selectedFields]);

  useEffect(() => {
    if (isMapReady && featureData.length && isEditActive && !submitted) {
      setSelectForEditFeatureId({
        id: featureData[0].id,
        timestamp: createUniqueTimestamp(),
      });
      setSelectedFeatureIds([featureData[0].id]);
    }
  }, [isMapReady, featureData, isEditActive, submitted]);

  useEffect(() => {
    if (isIdentifyActive) {
      setCheckedLayerItems([ladFieldId]);
    }
  }, [isIdentifyActive]);

  //on submit reset values
  useEffect(() => {
    if (submitted) {
      setSelectForEditFeatureId(undefined);

      const polygons = handlePolygons(data, undefined);
      const waypoints = handleWaypoints(data);

      setFeaturedData([...polygons, ...waypoints]);

      setTimeout(() => {
        setSelectForEditFeatureId({
          id: polygons[0].id,
          timestamp: createUniqueTimestamp(),
        });
      }, 200);
    }
  }, [submitted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      GeoMap.unmountMap();
    };
  }, []);

  useEffect(() => {
    if (zoomToMainFeature && featureData.length && isMapReady) {
      setZoomToFeature([featureData[0]]);
      setSelectedFeatureIds(featureData[0]['id']);
    }

    if (type === MapActionType.split && featureData.length && isMapReady) {
      setZoomToFeature([featureData[0], featureData[1]]);
      setSelectedFeatureIds([featureData[0]['id'], featureData[1]['id']]);
    }
  }, [zoomToMainFeature, featureData, isMapReady, type]);

  useEffect(() => {
    if (deletePromptDecision?.confirmed) {
      setIsDisableData((old) => ({
        ...old,
        confirmed: false,
      }));
    }
  }, [deletePromptDecision]);

  //on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        layerRef.current &&
        !layerRef.current.contains(event.target) &&
        event.target.className !== 'icon-layers'
      ) {
        setToggleLayerMenu(false);
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [layerRef]);

  useEffect(() => {
    if (removableSubFeature) {
      setFeaturedData((old) =>
        old.filter(
          (feature) =>
            feature.properties.ID !== removableSubFeature.properties.ID
        )
      );
    }
  }, [removableSubFeature]);

  // Function to parse a single geometry string
  const convertFromStringToCoord = (
    geometry: string,
    type: string = MapFieldType.polygon
  ) => {
    const parts = geometry.split(';');

    switch (type) {
      case MapFieldType.polygon:
        const cleanedString = parts[1].replace(/^POLYGON\(\(|\)\)$/g, '');
        const polygonParts = cleanedString
          .split(',(')
          .map((part) => part.replace(/[()]/g, ''));

        return polygonParts.map((part) => {
          return part.split(',').map((pair) => {
            const [x, y] = pair.trim().split(' ').map(Number);
            return [x, y];
          });
        });
      case MapFieldType.point:
        const match = parts[1].match(/POINT\(([-\d.]+) ([-\d.]+)\)/);

        return [parseFloat(match[1]), parseFloat(match[2])];
    }
  };

  const convertFromCoordToString = (coords: [Coordinate[]]) => {
    const polygonStrings = coords
      .map((polygon) => `(${polygon.map(([x, y]) => `${x} ${y}`).join(', ')})`)
      .join(',');

    return spatialReferenceSystemIdentifier + `;POLYGON(${polygonStrings})`;
  };

  const onEditFeatureSelected = (feature) => {
    if (feature.geometry.type === DrawTypes.Point) {
      return;
    }

    setField({
      area: feature.properties.AREA.replace(' ha', ''),
      geometry_area: feature.properties.AREA.replace(' ha', ''),
      geometry: convertFromCoordToString(feature.geometry.coordinates),
    });
  };

  const handlePolygons = useCallback(
    (innerData, colorCheck) => {
      return innerData
        .map((item, index) => {
          if (item.geometry) {
            const originalCoordinates = convertFromStringToCoord(item.geometry);
            const color = colorCheck && colorCheck(item)

            return {
              id: item.id || item.field_id || uuid(),
              geometry: {
                type: DrawTypes.Polygon,
                coordinates: originalCoordinates,
              },
              properties: {
                INDEX: index + 1,
                UUID: item.id,
                TITLE: item.name,
                BLOCKID: item.block_number,
                AREA: item.area + ' ha',
                LAD_NUMBER: item.lad_number,
                CROP: item.crop_value,
                CROP_ID: item.crop_id,
                CROP_GROUP: item.crop_group_value,
                CROP_GROUP_ID: item.crop_group_id,
                BREED: item.breed,
                _LABELSHORT:
                  (!isEditActive && !isDrawingActive ? true : !!index) &&
                  '{LAD_NUMBER}.',
                _LABELLONG:
                  (!isEditActive && !isDrawingActive ? true : !!index) &&
                  '{LAD_NUMBER}. {TITLE},\n{AREA}',
                _COLOR: getColor(color, true),
                _FILL: getColor(color),
                _LABELCOLOR: [0, 0, 0, 1],
                _SELECTLABELCOLOR: [255, 255, 255],
                _WIDTH: 2,
                _HOVERCOLOR: [255, 255, 255, 0.5],
                _HOVERFILL: [77, 175, 46, 0.6],
                _HOVERWIDTH: 4,
                _SELECTCOLOR: isEditActive ? getColor(color) : [255, 255, 255],
                _SELECTFILL: isEditActive
                  ? getColor(color)
                  : [24, 149, 8, 0.75],
                _SELECTWIDTH: 2,
                CHILDREN:
                  item.waypoints?.map((waypoint) => waypoint.id) || [], //connects waypoints from parent to child
              },
              type: 'Feature',
            };
          }

          return null;
        })
        .filter((e) => !!e);
    },
    [isEditActive, isDrawingActive]
  );

  const handleWaypoints = useCallback((innerData) => {
    let points = [];
    innerData.forEach((item) => {
      if (item.waypoints) {
        item.waypoints.forEach((e, index) => {
          const originalCoordinates = convertFromStringToCoord(
            e.point,
            MapFieldType.point
          );

          points.push({
            id: e.id || e.label,
            geometry: {
              type: DrawTypes.Point,
              coordinates: originalCoordinates,
            },
            properties: {
              INDEX: index + 1,
              ID: e.id || e.label,
              LABEL: e.label,
              PARENT: [item.lad_number], //connects waypoints from child to parent
              _POINTMAXRESOLUTION: 9,
              _COLOR: [77, 175, 46, 1],
              _FILL: 'white',
            },
            type: 'Feature',
          });
        });
      }
    });

    return points;
  }, []);

  const getColor = (color?: string, stroke?: boolean) => {
    if (color === 'green') {
      return [77, 175, 46, 0.4];
    }

    if (color === 'blue') {
      return [0, 94, 195, 0.4];
    }

    return [255, 255, 255, stroke ? 1 : 0.6];
  };

  const onMapFeatureSelect = (feature) => {
    const unmutateSelectedFields = [...selectedFields];

    const index = unmutateSelectedFields.findIndex(
      (obj) => obj.id === feature.id
    );

    if (index !== -1) {
      unmutateSelectedFields.splice(index, 1);
    } else {
      unmutateSelectedFields.push(featureData.find((obj) => obj.id === feature.id));
    }

    setSelectedFields(unmutateSelectedFields);

    return;
  };

  const onUnselectFeature = (feature) => {
    if (feature) {
      setSelectedFeatureIds((old) => old.filter((id) => id !== feature.id));

      const unmutateSelectedFields = [...selectedFields];
      const index = unmutateSelectedFields.findIndex(
        (obj) => obj.id === feature.id
      );
      unmutateSelectedFields.splice(index, 1);

      setSelectedFields(unmutateSelectedFields);
    }
  };

  const onHover = (feature) => {
    const parsed = feature
      ? (createTooltip(feature) as unknown as HTMLDivElement)
      : null;

    if (parsed !== tooltipHtml) {
      setTooltipHtml(parsed);
    }
  };

  const createTooltip = (feature) => {
    let htmlNode = document.createElement('div');
    htmlNode.classList.add('shadow');
    htmlNode.style.cssText =
      'background-color: white; padding: 8px; border-radius: 4px';

    if (feature.id) {
      let id = document.createElement('div');

      const hasLadNumber = columnConfiguration.some(
        (config) => config.field === 'lad_number'
      );

      if (feature.geometry.type === DrawTypes.Point) {
        id.textContent = feature.properties.LABEL;
      } else if (hasLadNumber) {
        if (feature.properties['LAD_NUMBER']) {
          id.textContent =
            t('pages.crop.fields_and_works.fields.tooltip_field_nr') +
            feature.properties['LAD_NUMBER'];
        }
      }

      htmlNode.append(id);
    } else {
      return null;
    }

    const hasBlockNumber = columnConfiguration.some(
      (config) => config.field === 'block_number'
    );
    if (feature.properties['BLOCKID'] && hasBlockNumber) {
      let blockNr = document.createElement('div');
      blockNr.textContent =
        t('pages.crop.fields_and_works.fields.tooltip_block_nr') +
        feature.properties['BLOCKID'];
      htmlNode.append(blockNr);
    }

    const hasTitle = columnConfiguration.some(
      (config) => config.field === 'name'
    );

    if (feature.properties['TITLE'] && hasTitle) {
      let placeName = document.createElement('div');
      placeName.textContent =
        t('pages.crop.fields_and_works.fields.tooltip_name') +
        feature.properties['TITLE'];
      htmlNode.append(placeName);
    }

    const hasArea = columnConfiguration.some(
      (config) => config.field === 'area'
    );
    if (feature.properties['AREA'] && hasArea) {
      let area = document.createElement('div');
      area.textContent =
        t('pages.crop.fields_and_works.fields.tooltip_area') +
        feature.properties['AREA'];
      htmlNode.append(area);
    }

    const hasCropGroup = columnConfiguration.some(
      (config) => config.field === 'crop_group_value'
    );
    if (feature.properties['CROP_GROUP'] && hasCropGroup) {
      let crop = document.createElement('div');
      crop.textContent =
        t('pages.crop.fields_and_works.fields.tooltip_crop_group') +
        feature.properties['CROP_GROUP'];
      htmlNode.append(crop);
    }

    const hasCrop = columnConfiguration.some(
      (config) => config.field === 'crop_value'
    );
    if (feature.properties['CROP'] && hasCrop) {
      let crop = document.createElement('div');
      crop.textContent =
        t('pages.crop.fields_and_works.fields.tooltip_crop') +
        feature.properties['CROP'];
      htmlNode.append(crop);
    }

    return htmlNode.outerHTML;
  };

  const onNewFeatureCreated = (feature) => {
    setField({
      season_id: season,
      area: feature.properties.AREA.replace(' ha', ''),
      geometry_area: feature.properties.AREA.replace(' ha', ''),
      geometry: convertFromCoordToString(feature.geometry.coordinates),
    });
  };

  const promptDeleteConfirmation = (feature) => {
    setIsDisableVisible(true);
    setIsDisableData({
      features: [feature],
      timestamp: createUniqueTimestamp(),
      confirmed: false,
    });
  };

  const onMapFeatureDoubleClick = (feature) => {
    if (feature.geometry.type === DrawTypes.Point) {
      return;
    }

    if (!isEditActive && !isDrawingActive) {
      history.push(
        route(CROP_FIELD_VIEW, {
          farmId: farmId,
          season: season,
          id: feature.properties.UUID,
          tab: TabsKeys.basic_info,
        })
      );
      return;
    }
  };

  const onSubFeaturesCreated = (features) => {
    if (!setField) {
      return;
    }

    if (features?.[0] && data?.[0]) {
      const feature = features[features.length - 1];
      const old = data[0]; //always first in array
      const waypointNextNumber = (old?.waypoints?.length ?? 0) + 1;
      const point = pointCoordToString(feature);

      const waypointInitialText = t(
        'pages.crop.fields_and_works.fields.waypoint_initial_text'
      );

      if (!old?.waypoints?.length) {
        setField({
          waypoints: [
            {
              point: point,
              label: waypointInitialText + waypointNextNumber,
              geometry_id: feature.id,
              id: feature.id,
            },
          ],
        });
        return;
      }

      const waypoints = features.map((fe) => {
        return {
          id: fe.properties.ID || fe.id,
          point: pointCoordToString(fe),
          label:
            old.waypoints.find(
              (waypoint) =>
                waypoint.geometry_id === fe.id ||
                (waypoint.id === fe.properties.ID && fe.properties.ID)
            )?.label || waypointInitialText + waypointNextNumber,
          geometry_id: fe.id,
        };
      });

      setField({
        waypoints: waypoints,
      });
    } else if (isMapReady) {
      setField({
        waypoints: [],
      });
    }
  };

  const pointCoordToString = (feature) => {
    return `${spatialReferenceSystemIdentifier};POINT(${feature.geometry.coordinates[0]} ${feature.geometry.coordinates[1]})`;
  };

  const onDelete = () => {
    setField({
      geometry: data[0]['initial_geometry'] ?? null,
      geometry_area: data[0]['initial_area'] ?? null,
      area: data[0]['initial_area'] ?? null,
      waypoints: data[0]['initial_waypoints']
        ? data[0]['initial_waypoints']
        : [],
    });

    if (!data[0]['initial_geometry']) {
      setIsDisableData({
        ...deletePromptDecision,
        confirmed: true,
      });
      setSelectForEditFeatureId(null);
      setIsDisableVisible(false);
    }
  };

  //hand LAD checked and hovered
  useMemo(() => {
    const hoveredState = [
      mappingState,
      fieldSearchForMappingState,
      currentLadStatusWithLadState,
    ].find((state) => state.hovered && state.hovered.geometry);

    const ladDataHovered = [hoveredState && hoveredState.hovered]
      .filter(Boolean)
      .filter((field) => field.geometry);

    const ladData = [
      ...fieldsNotRegisteredInLadState.checked,
      ...mappingState.checked,
      ...fieldSearchForMappingState.checked,
    ]
      .filter(Boolean)
      .filter((field) => field.geometry);

    let dataWithCoords = [];

    if (ladDataHovered?.length) {
      dataWithCoords = handlePolygons(
        ladDataHovered,
        (item) =>  item.stored === ladDataStoredType ? 'green' : 'blue'
      );
    }

    const mainDataWithCoords = handlePolygons(
      ladData,
      (item) =>  item.stored === ladDataStoredType ? 'green' : 'blue'
    );

    dataWithCoords = [...mainDataWithCoords, ...dataWithCoords];

    //for some reason triggers rerender even though nothing has changed
    if (JSON.stringify(featureData) !== JSON.stringify(dataWithCoords)) {
      if (dataWithCoords?.[0]?.geometry) {
        setFeaturedData(dataWithCoords);
        setZoomToFeature([dataWithCoords[0]]);
      }
    }

    return ladData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mappingState,
    fieldSearchForMappingState,
    currentLadStatusWithLadState,
    fieldsNotRegisteredInLadState.checked,
    handlePolygons,
  ]);

  const getSearchValue = (feature: GeoJSONFeature) => {
    const searchConf = searchInputs.find(
      (s) => s.id === feature.properties['service']
    );

    return feature.properties[searchConf.labelField];
  };

  const splitted = () => {
    const splittedFeature = featureData[0]; //always first in array

    const mapped: RedirectFeature[] = splitFeatures.map((feature, index) => {
      return {
        geometry: convertFromCoordToString(feature.geometry.coordinates),
        area: feature.properties.AREA.replace(' ha', ''),
        name: feature.properties.TITLE + '-' + (index + 1),
        crop_id: {
          label: feature.properties.CROP,
          value: splittedFeature.properties.CROP_ID,
        },
        crop_group_id: {
          label: splittedFeature.properties.CROP_GROUP,
          value: splittedFeature.properties.CROP_GROUP_ID,
        },
        block_number: splittedFeature.properties.BLOCKID,
        lad_number: splittedFeature.properties.LAD_NUMBER + '-' + (index + 1),
        breed: splittedFeature.properties.BREED,
        mergeCandidates: [],
        other_fields: data.filter((field) => field.id !== splittedFeature.id),
      };
    });

    dispatch(setSplitGeometries(mapped));

    history.push(
      route(CROP_FIELD_SPLIT, {
        farmId: farmId,
        season: season,
        id: splittedFeature.id,
      })
    );
  };

  const merged = (feature) => {
    const largestAreaEntry = mergeFeatures.reduce((max, item) => {
      return item.properties.CROP_ID != null &&
        parseFloat(item.properties.AREA.replace(' ha', '')) >
          parseFloat(max.properties.AREA.replace(' ha', ''))
        ? item
        : max;
    }, mergeFeatures[0]);

    const mainEntry = featureData.find(
      (field) => (field.properties.UUID = largestAreaEntry.properties.UUID)
    );

    const newName = mergeFeatures
      .map((feat) => {
        return feat.properties.TITLE;
      })
      .join('; ');

    const newLadNumber = mergeFeatures
      .map((feat) => {
        return feat.properties.LAD_NUMBER;
      })
      .join('; ');

    dispatch(
      setMergeGeometries({
        geometry: convertFromCoordToString(feature.geometry.coordinates),
        area: feature.properties.AREA.replace(' ha', ''),
        name: newName,
        crop_id: {
          label: largestAreaEntry.properties.CROP,
          value: largestAreaEntry.properties.CROP_ID,
        },
        crop_group_id: {
          label: largestAreaEntry.properties.CROP_GROUP,
          value: largestAreaEntry.properties.CROP_GROUP_ID,
        },
        lad_number: newLadNumber,
        breed: mainEntry.properties.BREED,
        mergeCandidates: mergeCandidates,
        other_fields: data.filter(
          (field) =>
            !mergeFeatures.map((feature) => feature.id).includes(field.id)
        ),
      })
    );

    history.push(
      route(CROP_FIELDS_MERGE.concat('?fields=:fields'), {
        farmId: farmId,
        season: season,
        fields: mergeFeatures.map((feature) => feature.id),
      })
    );
  };

  const onSearchResults = (features) => {
    if (features.length === 1) {
      const feature = features[0];

      feature.properties = {
        ...feature.properties,
        _COLOR: [255, 0, 100, 1],
        _FILL: [255, 0, 100, 0.5],
        _WIDTH: 2,
      };

      setPreviewFeatures([feature]);
      setSearchOptions([]);
      setShowSearchDropdown(false);

      return;
    }

    if (features !== searchOptions) {
      setShowSearchDropdown(true);
      setSearchOptions(
        features.sort((a, b) => {
          const aValue = getSearchValue(a);
          const bValue = getSearchValue(b);

          return aValue.localeCompare(bValue);
        })
      );
    }
  };

  const onMergeCheckboxClick = (e, feature) => {
    const checkboxValue = e.target.checked;

    if (checkboxValue) {
      setMergeFeatures((old) => [...old, feature]);
      return;
    }

    setMergeFeatures((old) => old.filter((feat) => feat.id !== feature.id));
  };

  const onSelectForMerge = (features) => {
    if (features.length !== mergeFeatures.length) {
      setMergeFeatures(features);
    }
  };

  const onSearchClick = (result) => {
    searchInputRef.current.value = getSearchValue(result);

    let unmutate = { ...result };

    unmutate.properties = {
      ...result.properties,
      _COLOR: [255, 0, 100, 1],
      _FILL: [255, 0, 100, 0.5],
      _WIDTH: 2,
    };

    setPreviewFeatures([unmutate]);
    setShowSearchDropdown(false);
    setSearchOptions([]);
  };

  const onMerge = () => {
    setMergePromptDecision({
      features: mergeFeatures,
      timestamp: createUniqueTimestamp(),
      confirmed: true,
    });
  };

  const onIdentify = (feature) => {
    const hectare = parseFloat(feature.properties?.['SHAPE.AREA']) / 10000;

    setField({
      area: hectare.toFixed(4),
      geometry_area: hectare.toFixed(4),
      geometry: convertFromCoordToString(feature.geometry.coordinates),
      block_number: feature.properties?.['Bloka numurs'],
    });

    setHasIdentified(true);
  };

  const onLayerCheck = (event, layer) => {
    if (isIdentifyActive && layer.id === ladFieldId) {
      return;
    }

    setCheckedLayerItems(
      event.target.checked
        ? [...checkedLayerItems, layer.id]
        : checkedLayerItems.filter((item) => item !== layer.id)
    );

    if (!event.target.checked && layer.id === 'Zmni') {
      setShowLegends(false);
    }
  };

  const onAction = () => {
    setShowLegends(false);
    setShowSearchDropdown(false);
    setToggleLayerMenu(false);
    searchInputRef.current.value = null;
  };

  const onMergeCancel = () => {
    setValue('isMergeActive', false);
    setSelectForEditFeatureId({
      id: featureData[0].id,
      timestamp: createUniqueTimestamp(),
    });
    setMergeCandidates([]);
    setMergeFeatures([]);
  };

  const onSplitCancel = () => {
    setValue('isSplitActive', false);
    setSplitFeatures([]);
  };

  const startExtentFromFeatures = useMemo(() => {
    if (!featureData?.length) {
      return undefined;
    }

    if (zoomToMainFeature || type === MapActionType.edit) {
      return [featureData[0]];
    }

    return featureData;
  }, [featureData, type, zoomToMainFeature]);

  if (
    startExtentFromFeatures === undefined &&
    !dataLoaded &&
    type !== MapActionType.lad
  ) {
    return <></>;
  }

  return (
    <>
      <Spinner
        visible={!isMapReady}
        size="lg"
        className={indexCss['custom-spinner']}
      />
      <MapReactComponent
        tooltipHtml={tooltipHtml}
        featureData={featureData}
        onMapFeatureSelect={onMapFeatureSelect}
        onUnselectFeature={onUnselectFeature}
        onHover={onHover}
        onMapFeatureDoubleClick={onMapFeatureDoubleClick}
        zoomToFeatures={zoomToFeature}
        padding={{ left: 0, duration: 0 }}
        selectedFeatureIds={selectedFeatureIds}
        isDrawingActive={isDrawingActive}
        onNewFeatureCreated={onNewFeatureCreated}
        onSubFeaturesCreated={onSubFeaturesCreated}
        newFeaturePromptDecision={{
          confirmed: false,
          features: [],
          timestamp: createUniqueTimestamp(),
        }}
        promptDeleteConfirmation={promptDeleteConfirmation}
        isEditActive={isEditActive && !!selectForEditFeatureId}
        onEditFeatureSelected={onEditFeatureSelected}
        onSplitCompleted={() => false}
        isMergeActive={isMergeActive}
        onFoundMergeCandidateIds={setMergeCandidates}
        onMergeCompleted={merged}
        onSelectForMerge={onSelectForMerge}
        selectToMerge={mergeFeatures}
        onLayersLoaded={(layerData) =>
          setLegendList(
            layerData.find((layer) => layer.id === layerWithLegend)?.legends ??
              []
          )
        }
        onLayersMenuToggle={() => {
          setToggleLayerMenu((old) => !old);
        }}
        isLayersMenuVisible={toggleLayerMenu}
        enabledLayers={checkedLayerItems}
        mergePromptDecision={mergePromptDecision}
        isIdentifyActive={isIdentifyActive}
        onIdentify={onIdentify}
        searchParams={searchParams}
        onSearchResults={onSearchResults}
        previewFeatures={previewFeatures}
        selectForEditFeatureId={selectForEditFeatureId}
        deletePromptDecision={deletePromptDecision}
        isSplitActive={isSplitActive}
        onSplitChanged={setSplitFeatures}
        onSplitCancel={onSplitCancel}
        mapContainerRef={mapContainerRef}
        removableSubFeature={removableSubFeature}
        onAction={onAction}
        onMapReady={() => setIsMapReady(true)}
        startExtentFromFeatures={startExtentFromFeatures}
        mergeStyles={{
          candidate: {
            color: [143, 0, 255, 1],
            fill: [255, 255, 255, 0.7],
            width: 1,
          },
          selected: {
            color: [143, 0, 255, 1],
            fill: [143, 0, 255, 0.8],
            width: 2,
          },
        }}
        splitStyles={{
          candidate: {
            color: [143, 0, 255, 1],
            fill: [143, 0, 255, 0.8],
            width: 2,
          },
          splitted: {
            color: [143, 0, 255, 1],
            fill: [255, 255, 255, 0.25],
            width: 3,
          },
        }}
      />

      {isDisableVisible && (
        <div className={indexCss['delete-container']}>
          <span className={indexCss['modal-text']}>
            {t('pages.crop.fields_and_works.fields.delete_prompt')}
          </span>
          <div className={indexCss['button-container']}>
            <ButtonComponent
              name="no"
              label={t('pages.crop.vaad_liz_reports_v2.remove_modal.cancel')}
              styling={ButtonStyling.cancel}
              click={() => setIsDisableVisible(false)}
              ignoreValidation
            />
            <ButtonComponent
              name="yes"
              label={t('pages.crop.vaad_liz_reports_v2.remove_modal.ok')}
              click={onDelete}
              ignoreValidation
            />
          </div>
        </div>
      )}

      {showError && (
        <div className={indexCss['delete-container']}>
          <span className={indexCss['modal-text']}>
            {t('pages.crop.fields_and_works.fields.' + showError)}
          </span>
          <div className={indexCss['button-container']}>
            <ButtonComponent
              name="yes"
              label={t('pages.crop.fields_and_works.fields.agree')}
              click={() => setShowError(undefined)}
              ignoreValidation
            />
          </div>
        </div>
      )}

      {toggleLayerMenu && (
        <div ref={layerRef} className={indexCss['layer-menu']}>
          <span className={indexCss['modal-text']}>
            {t('pages.crop.fields_and_works.fields.layer_list')}
          </span>
          <div>
            {availableLayers.map((layer) => (
              <div className={indexCss['layer-item']}>
                <input
                  type="checkbox"
                  id={layer.id}
                  name={layer.id}
                  className={css['checkbox']}
                  checked={!!checkedLayerItems.includes(layer.id)}
                  onChange={(event) => onLayerCheck(event, layer)}
                />
                <span>{t(layer.title)}</span>
              </div>
            ))}
            <div
              onClick={() => {
                setShowLegends(true);
                setToggleLayerMenu(false);
              }}
              className={indexCss['legend-button']}
            >
              ({t('pages.crop.fields_and_works.fields.open_legends')})
            </div>
          </div>
        </div>
      )}

      {showLegends && (
        <div
          className={indexCss['legend-menu']}
          style={{ height: mapContainerRef.current.clientHeight }}
        >
          <div className={indexCss['legend-title']}>
            {t('pages.crop.fields_and_works.fields.zmni_legend_name')}
          </div>
          <div className={indexCss['legend-list']}>
            {legendList.map((list, index) => (
              <LegendItem key={index} list={list} />
            ))}
          </div>
          <FontAwesomeIcon
            onClick={() => setShowLegends(false)}
            className={indexCss['close-icon']}
            icon={faTimes}
          />
        </div>
      )}

      <div className={indexCss['search-container']}>
        <input
          ref={searchInputRef}
          id="search"
          name="search"
          type="search"
          className={indexCss['search-input']}
          placeholder={t('pages.crop.fields_and_works.fields.search')}
          onChange={(e) => debounceSearch(e.target.value)}
          onKeyDown={(event) => {
            if (event.keyCode === enterKeyCode) {
              event.preventDefault();
            }

            return true;
          }}
          autoComplete="off"
        />
        <div className={indexCss['search-list']}>
          {showSearchDropdown &&
            searchOptions.map((result, i) => (
              <div
                className={indexCss['search-list-options']}
                key={'option-' + i}
                onClick={() => onSearchClick(result)}
              >
                {getSearchValue(result)}
              </div>
            ))}
        </div>
      </div>

      {isSplitActive && (
        <div className={indexCss['split-container']}>
          <div className={indexCss['split-header']}>
            {t('pages.crop.fields_and_works.fields.split.split_header')}
          </div>
          {splitFeatures.map((feature, index) => (
            <div className={indexCss['split-feature']}>{`${
              featureData[0].properties.LAD_NUMBER
            }-${index + 1}, ${feature.properties.TITLE}-${index + 1}, ${
              feature.properties.AREA
            } ${
              feature.properties.CROP ? ', ' + feature.properties.CROP : ''
            }`}</div>
          ))}
          <div className={indexCss['split-buttons']}>
            <ButtonComponent
              name="confirm"
              label={t('pages.crop.fields_and_works.fields.split.split')}
              click={splitted}
              disabled={splitFeatures.length !== splitLength}
              ignoreValidation
            />
          </div>
          <FontAwesomeIcon
            onClick={() => setValue('isSplitActive', false)}
            className={indexCss['close-icon']}
            icon={faTimes}
          />
        </div>
      )}

      {isMergeActive && (
        <div className={indexCss['split-container']}>
          <div className={indexCss['merge-header']}>
            {t('pages.crop.fields_and_works.fields.merge.merge_header')}
          </div>
          <div className={indexCss['merge-sub-header']}>
            {t('pages.crop.fields_and_works.fields.merge.merge_sub_header')}
          </div>
          {featureData
            .filter((feature) => mergeCandidates.includes(feature.id))
            .map((feature) => (
              <div className={indexCss['merge-feature']}>
                <input
                  type="checkbox"
                  checked={
                    !!mergeFeatures
                      .map((feature) => feature.id)
                      .includes(feature.id)
                  }
                  className={checkBoxCss['checkbox']}
                  onChange={(e) => onMergeCheckboxClick(e, feature)}
                />
                {`${feature.properties.LAD_NUMBER}, ${
                  feature.properties.TITLE
                }, ${feature.properties.AREA} ${
                  feature.properties.CROP ? ', ' + feature.properties.CROP : ''
                }`}
              </div>
            ))}
          <div className={indexCss['merge-buttons']}>
            <ButtonComponent
              name="confirm"
              label={t('pages.crop.fields_and_works.fields.merge.merge')}
              disabled={mergeFeatures.length <= 1}
              click={onMerge}
              ignoreValidation
            />
          </div>
          <FontAwesomeIcon
            onClick={onMergeCancel}
            className={indexCss['close-icon']}
            icon={faTimes}
          />
        </div>
      )}
    </>
  );
};

interface MapProps extends IMapComponentProps {
  mapContainerRef: RefObject<HTMLDivElement>;
}

export const MapReactComponent = (props: MapProps) => {
  useEffect(() => {
    if (props.mapContainerRef.current) {
      GeoMap.renderMap(props, props.mapContainerRef.current);
    }
  }, [props]);

  return (
    <div ref={props.mapContainerRef} className={indexCss['map-container']} />
  );
};

export default NewMap;
